import { useCallback, useEffect, useRef } from 'react';
import { useToastNotificationContext } from '@sendible/design-system/src';
import iframeUtils from '@sendible/common/utils/iframe-utils';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { useTranslation } from 'react-i18next';
import { useGoogleAuth } from './GoogleAuthContext';
import { GoogleService } from './types';

type GooglePicker = {
  setVisible: (visible: boolean) => void;
};

type GooglePickerDoc = {
  id: string;
  name?: string;
  mimeType?: string;
  [key: string]: unknown;
};

type GooglePickerCallbackData = {
  action: string;
  docs?: GooglePickerDoc[];
};
export const getDownloadUrl = (fileId: string): string => {
  return `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media&key=${GOOGLE_API_KEY}`;
};
export const useGoogleDriveService = () => {
  const { isAuthenticated, getServiceToken, isLoading: authLoading, authorize, authError, setAuthError, logout } = useGoogleAuth();
  const { t } = useTranslation(['compose_box']);
  const { showToastNotification } = useToastNotificationContext();
  const pickerRef = useRef<GooglePicker | null>(null);

  useEffect(() => {
    if (authError) {
      showToastNotification({
        notificationText: authError,
        type: NotificationTypeEnum.Error,
        showCloseButton: true,
      });
      setAuthError(undefined);
    }
  }, [authError, showToastNotification, setAuthError]);

  const isAuthenticatedPickerReady = isAuthenticated(GoogleService.Picker) && !!window.gapi?.client;

  const authorizeGoogleDrive = useCallback(async (): Promise<void> => {
    await authorize(GoogleService.Picker);
  }, [authorize]);

  const closePicker = useCallback(() => {
    if (pickerRef.current) {
      pickerRef.current.setVisible(false);
      pickerRef.current = null;
    }

    const pickerIframes = document.querySelectorAll('.picker-dialog-bg, .picker-dialog, iframe.picker');

    pickerIframes.forEach((iframe) => iframe.remove());
  }, []);

  const openDrivePicker = useCallback(
    (onPicked: (files: GooglePickerDoc[]) => void, onCancel?: () => void) => {
      const token = getServiceToken(GoogleService.Picker);
      const isReady =
        isAuthenticated(GoogleService.Picker) && !!window.gapi?.client && !!token?.access_token && !!window.gapi && !!window.google?.picker;

      if (!isReady) {
        throw new Error('Google API Picker not loaded');
      }

      if (pickerRef.current) {
        return;
      }

      if (!GOOGLE_API_KEY) {
        throw new Error('Google API Key is missing');
      }

      const checkMediaAccessibility = async (docs: GooglePickerDoc[]): Promise<Map<string, boolean>> => {
        const checkMedia = (doc: GooglePickerDoc): Promise<[string, boolean]> => {
          return new Promise((resolve) => {
            const url = getDownloadUrl(doc.id);
            const isVideo = doc.mimeType?.startsWith('video/');

            if (isVideo) {
              const video = document.createElement('video');

              video.src = url;
              video.onloadedmetadata = () => resolve([doc.id, true]);
              video.onerror = () => resolve([doc.id, false]);
            } else {
              const img = new Image();

              img.src = url;
              img.onload = () => resolve([doc.id, true]);
              img.onerror = () => resolve([doc.id, false]);
            }
          });
        };

        const results = await Promise.all(docs.map((doc) => checkMedia(doc)));

        return new Map(results);
      };

      window.gapi.load('picker', () => {
        if (!window.google.picker) {
          throw new Error('Google Picker is not available');
        }

        const docsView = new window.google.picker.DocsView()
          .setIncludeFolders(false)
          .setMimeTypes('application/vnd.google-apps.folder,image/png,image/jpeg,image/gif,image/webp,video/mp4,video/quicktime,application/pdf')
          .setSelectFolderEnabled(true);

        const pickerToken = getServiceToken(GoogleService.Picker);

        // Add validation for OAuth token
        if (!pickerToken?.access_token) {
          throw new Error('Invalid OAuth token');
        }

        const picker: GooglePicker = new window.google.picker.PickerBuilder()
          .addView(docsView)
          .addView(window.google.picker.ViewId.DOCS_IMAGES)
          .addView(window.google.picker.ViewId.DOCS_VIDEOS)
          .setOAuthToken(pickerToken?.access_token)
          .setDeveloperKey(GOOGLE_API_KEY)
          .setOrigin(iframeUtils.getParentDomain())
          .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
          .setCallback(async (data: GooglePickerCallbackData) => {
            if (!window?.google?.picker) {
              return;
            }
            if (data.action === window.google.picker.Action.PICKED && data.docs) {
              const accessibilityMap = await checkMediaAccessibility(data.docs);

              const validDocs = data.docs.filter((doc) => accessibilityMap.get(doc.id));
              const hasInvalidDocs = Array.from(accessibilityMap.values()).some((isAccessible) => !isAccessible);

              if (hasInvalidDocs) {
                showToastNotification({
                  notificationText: `${t('GoogleFilesNotAccessible')}<br />${t('SetGoogleDriveSharing')}`,
                  type: NotificationTypeEnum.Error,
                  showCloseButton: true,
                });
              }

              if (validDocs.length > 0) {
                onPicked(validDocs);
              } else if (onCancel) {
                onCancel();
              }
            } else if (data.action === window.google.picker.Action.CANCEL && onCancel) {
              onCancel();
            }
          })
          .build();

        if (!picker) {
          throw new Error('Failed to create Google Picker');
        }

        picker.setVisible(true);
        pickerRef.current = picker;
      });
    },
    [getServiceToken, GOOGLE_API_KEY, showToastNotification, t, isAuthenticated]
  );

  return {
    isAuthenticatedPickerReady,
    isLoading: authLoading,
    authorizeGoogleDrive,
    openDrivePicker,
    closePicker,
    logout,
  };
};
